import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import commonFr from "../../locales/fr/common.json";
import adminFr from "../../locales/fr/admin.json";
import authFr from "../../locales/fr/auth.json";
import coachFr from "../../locales/fr/coach.json";
import studentFr from "../../locales/fr/student.json";
import superadminFr from "../../locales/fr/superadmin.json";
import registrationFr from "../../locales/fr/registration.json";
import legalFr from "../../locales/fr/legal.json";

export const SUPPORTED_LOCALES = ["fr", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE = "fr";

const STORAGE_KEY = "locale";

export const NAMESPACES = ["common", "admin", "auth", "coach", "student", "superadmin", "registration", "legal"] as const;

/**
 * The default locale ships inside the entry bundle: every first paint needs it,
 * and it is the fallback for any missing key. Other locales are separate chunks
 * (see {@link LAZY_LOCALES}) fetched only when actually selected.
 */
export const resources = {
  fr: { common: commonFr, admin: adminFr, auth: authFr, coach: coachFr, student: studentFr, superadmin: superadminFr, registration: registrationFr, legal: legalFr },
} as const;

/** Locales that are not in the entry bundle; each maps to its own chunk. */
const LAZY_LOCALES: Partial<Record<Locale, () => Promise<{ default: Record<string, Record<string, unknown>> }>>> = {
  en: () => import("./locale-en"),
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** The locale this visitor picked here, if any (storage may be unavailable in private mode). */
function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Saved choice, else the browser's preferred language, else French. */
export function detectLocale(): Locale {
  const stored = storedLocale();
  if (stored) return stored;
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.toLowerCase().split("-")[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

/** Current UI locale as an Intl tag, for date/number formatting. */
export function intlLocale(): string {
  return i18n.language === "en" ? "en-US" : "fr-FR";
}

function applyDocumentLang(locale: string) {
  document.documentElement.lang = locale;
}

/** Loads a locale that is not in the entry bundle, once. */
async function ensureLocale(locale: Locale): Promise<void> {
  if (i18n.hasResourceBundle(locale, "common")) return;
  const load = LAZY_LOCALES[locale];
  if (!load) return;
  const { default: bundle } = await load();
  for (const [ns, res] of Object.entries(bundle)) {
    i18n.addResourceBundle(locale, ns, res, true, true);
  }
}

/**
 * Shows the app in a locale without remembering the choice: for pages that belong to someone
 * other than the visitor (a club's registration page opens in the club's language).
 */
export async function applyLocale(locale: Locale) {
  await ensureLocale(locale);
  return i18n.changeLanguage(locale);
}

/** Switches the UI language (fetching it if needed) and remembers it for the next visit. */
export async function setLocale(locale: Locale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // best-effort persistence
  }
  return applyLocale(locale);
}

i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,
  defaultNS: "common",
  ns: NAMESPACES,
  // React already escapes. {{appName}} is the product name the server renders (APP_NAME).
  interpolation: { escapeValue: false, defaultVariables: { appName: document.querySelector('meta[name="application-name"]')?.getAttribute("content") ?? "Judo Companion" } },
});

applyDocumentLang(DEFAULT_LOCALE);
i18n.on("languageChanged", applyDocumentLang);

/**
 * Resolves once this visitor's locale is actually in memory. The app mounts
 * after it, so an English visitor never sees a frame of missing keys.
 */
export const i18nReady: Promise<void> = (async () => {
  const detected = detectLocale();
  await ensureLocale(detected);
  if (detected !== i18n.language) await i18n.changeLanguage(detected);
})();

export default i18n;
